import { normalize } from "normalizr";
import * as React from "react";
import { change, getFormValues, initialize } from "redux-form";
import { all, call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import {
    ARTICLE_CREATE_EDIT_FORM_NAME,
    ArticleFormData,
} from "@/mk2/apps/blogs/components/ArticleCreateEditForm";
import { ArticleFetchNormalizedResponse } from "@/mk2/apps/blogs/containers/Article/Article.actions";
import {
    ARTICLE_EDIT_CHANGE_EDITOR_TYPE_TRIGGER,
    ARTICLE_EDIT_FETCH_TRIGGER,
    ARTICLE_EDIT_INSERT_IMAGE_TRIGGER,
    ARTICLE_EDIT_SAVE_ARTICLE_TRIGGER,
    ArticleEditChangeEditorTypeTriggerAction,
    ArticleEditFetchTriggerAction,
    ArticleEditInsertImageNormalizedResponse,
    ArticleEditInsertImageTriggerAction,
    ArticleEditSaveArticleTriggerAction,
    ArticleEditSaveNormalizedResponse,
    articleEditChangeEditorTypeApi,
    articleEditFetchApi,
    articleEditInsertImageApi,
    articleEditSaveArticleApi,
} from "@/mk2/apps/blogs/containers/ArticleEdit/ArticleEdit.actions";
import {
    blogArticleAstToHTML,
    cleanMarkdownArticleFromFroala,
    insertUploadingImagesToTheEditor,
    prepareHTMLArticleForFroala,
    prepareMarkdownArticleForFroala,
    revertReplacedTagsOfArticleScripts,
} from "@/mk2/apps/blogs/containers/ArticleEdit/ArticleEdit.helpers";
import { advancedArticleUrl } from "@/mk2/apps/blogs/urls";
import { SmartTextareaEditorType } from "@/mk2/components/forms/SmartTextareaField";
import { photosUploadTrigger } from "@/mk2/containers/PhotosUpload/PhotosUpload.actions";
import {
    XHRAction,
    XHRFormError,
    XHRGetError,
    handleXHRGetErrorSaga,
    normalizeError,
} from "@/mk2/helpers/api";
import { handleXHRFormErrorSaga, handleXHRFormSuccessSaga } from "@/mk2/helpers/form";
import { getPhotoPreview } from "@/mk2/helpers/photos";
import { scrollDocumentTo } from "@/mk2/helpers/scrolling";
import { getLogger } from "@/mk2/logger";
import {
    AST,
    ASTAdditionalDataFromServer,
    BlogPostEntity,
    BlogPostSchema,
    PhotoPreviewSchema,
} from "@/mk2/schemas";
import { getDenormalizedEntity } from "@/mk2/selectors";
import { HistoryAction } from "@/mk2/services/browserHistory";
import {
    ERROR_COULD_NOT_PERFORM_ACTION,
    PHOTOBLOG_ARTICLE_CONTENT_IS_CONVERTING,
} from "@/mk/autogenerated/translations/ArticleEdit.sagas.5146278f896794aa5e25c68e24aa323b";
import {
    blogs_api_article_create_url,
    blogs_api_article_edit_url,
    blogs_api_article_get_url,
    blogs_api_article_parse_url,
} from "@/mk/urls/functions";

const logger = getLogger("blogs/ArticleEdit.sagas");

interface ApiResponseArticleGet {
    body: {
        entities: ArticleFetchNormalizedResponse["entities"];
        result: {
            post: number;
        };
    };
}

function* fetchArticleEdit({
    username,
    articleSlug,
    xhr,
    location,
    routeNavigate,
}: ArticleEditFetchTriggerAction & XHRAction & HistoryAction) {
    yield put(articleEditFetchApi.request(username, articleSlug));

    try {
        const response: ApiResponseArticleGet = yield call(() =>
            xhr.get(blogs_api_article_get_url(), {
                username,
                articleSlug,
                articleOpened: false,
                articleEdited: true,
            }),
        );

        // in the case of client side rendering fix scripts here
        // in the case of server side rendering fix scripts in the js2/helpers/init.tsx (where the INITIAL_STATE is loaded)
        if (typeof window !== "undefined") {
            const post = response.body.entities[BlogPostSchema.key][response.body.result.post];
            post.scripts = post.scripts
                ? revertReplacedTagsOfArticleScripts(post.scripts)
                : post.scripts;
        }

        yield put(
            articleEditFetchApi.success(username, articleSlug, {
                entities: response.body.entities,
                result: response.body.result,
            }),
        );
    } catch (error) {
        yield handleXHRGetErrorSaga(error as XHRGetError, location, routeNavigate, logger);
        yield put(articleEditFetchApi.failure(username, articleSlug, normalizeError(error)));
    }
}

interface ApiResponseArticleParseDraft {
    body: {
        bodyAST: AST;
        bodyASTAdditionalData: ASTAdditionalDataFromServer;
        countTopicContributors: { [topicSlug: string]: number };
        bodyMD: string;
        unparsedParts: string;
    };
}

function* changeEditorType({
    username,
    articleSlug,
    editorType: newEditorType,
    xhr,
    location,
    routeNavigate,
}: ArticleEditChangeEditorTypeTriggerAction & XHRAction & HistoryAction) {
    yield put(articleEditChangeEditorTypeApi.request(username, articleSlug, newEditorType));

    try {
        const currentValues: ArticleFormData = yield select(
            getFormValues(ARTICLE_CREATE_EDIT_FORM_NAME),
        );
        const oldEditorType = currentValues.editorType;
        let oldBody = currentValues.body;

        yield put(
            change(ARTICLE_CREATE_EDIT_FORM_NAME, "body", PHOTOBLOG_ARTICLE_CONTENT_IS_CONVERTING),
        );

        if (oldEditorType === SmartTextareaEditorType.MARKDOWN) {
            oldBody = cleanMarkdownArticleFromFroala(oldBody);
        }
        const response: ApiResponseArticleParseDraft = yield call(() =>
            xhr.post(blogs_api_article_parse_url(), {
                body: oldBody,
                editorType: oldEditorType,
            }),
        );

        const ast = response.body.bodyAST;
        const md = response.body.bodyMD;

        // Disabled checking unparsed markdown chunks like "**", "//" because posts and comments are largely
        // heterogeneous so probably they are typed by purpose, and are not an incorrect markdown usage (unlike in
        // the wiki articles)
        // const unparsedParts = response.body.unparsedParts;

        let body = null;

        if (newEditorType === SmartTextareaEditorType.RICHTEXT) {
            body = prepareHTMLArticleForFroala(
                blogArticleAstToHTML(
                    ast,
                    currentValues.photos,
                    response.body.countTopicContributors,
                    response.body.bodyASTAdditionalData,
                ),
            );
        }
        if (newEditorType === SmartTextareaEditorType.MARKDOWN) {
            body = prepareMarkdownArticleForFroala(md);
        }

        yield put(change(ARTICLE_CREATE_EDIT_FORM_NAME, "editorType", newEditorType));
        yield put(change(ARTICLE_CREATE_EDIT_FORM_NAME, "body", body));
        yield put(change(ARTICLE_CREATE_EDIT_FORM_NAME, "bodyAST", ast));

        yield put(articleEditChangeEditorTypeApi.success(username, articleSlug, newEditorType, {}));
    } catch (error) {
        yield handleXHRGetErrorSaga(error as XHRGetError, location, routeNavigate, logger);
        yield put(
            articleEditChangeEditorTypeApi.failure(
                username,
                articleSlug,
                newEditorType,
                normalizeError(error),
            ),
        );
    }
}

interface ApiResponseArticleSave {
    body: {
        entities: ArticleEditSaveNormalizedResponse["entities"];
        result: ArticleEditSaveNormalizedResponse["result"];
    };
}

function* saveArticle({
    username,
    articleSlug,
    articleId,
    articleFd,
    saveActionType,
    xhr,
    location,
    routeNavigate,
}: ArticleEditSaveArticleTriggerAction & XHRAction & HistoryAction) {
    yield put(
        articleEditSaveArticleApi.request(
            username,
            articleSlug,
            articleId,
            articleFd,
            saveActionType,
        ),
    );

    try {
        const articleFdClean: ArticleFormData = {
            ...articleFd,
            body:
                articleFd.editorType === SmartTextareaEditorType.MARKDOWN
                    ? cleanMarkdownArticleFromFroala(articleFd.body)
                    : articleFd.body,
        };

        const response: ApiResponseArticleSave = !articleId
            ? yield call(() =>
                  xhr.post(blogs_api_article_create_url(), {
                      articleFd: articleFdClean,
                      saveActionType,
                  }),
              )
            : yield call(() =>
                  xhr.post(blogs_api_article_edit_url(), {
                      articleId,
                      articleFd: articleFdClean,
                      saveActionType,
                  }),
              );

        const normalizedResponse: ArticleEditSaveNormalizedResponse = {
            entities: response.body.entities,
            result: response.body.result,
        };

        const post = getDenormalizedEntity<BlogPostEntity>(
            normalizedResponse,
            BlogPostSchema,
            response.body.result.post,
        );

        const gotoUrl = advancedArticleUrl(post);

        // The handleXHRFormSuccessSaga has actions (form)reset and (form)destroy and between these two steps the
        // Froala editor clean/change the article body and it became dirty and destroying invoke an warning alert.
        // The problem is solved with re-initializing with a param `keepDirty` to avoid alert.
        yield put(initialize(ARTICLE_CREATE_EDIT_FORM_NAME, {}, true));
        yield handleXHRFormSuccessSaga(ARTICLE_CREATE_EDIT_FORM_NAME, routeNavigate, gotoUrl);
        yield put(
            articleEditSaveArticleApi.success(
                username,
                articleSlug,
                articleId,
                articleFd,
                saveActionType,
                normalizedResponse,
            ),
        );
    } catch (error) {
        yield handleXHRFormErrorSaga(ARTICLE_CREATE_EDIT_FORM_NAME, error as XHRFormError, logger);
        yield put(
            articleEditSaveArticleApi.failure(
                username,
                articleSlug,
                articleId,
                articleFd,
                saveActionType,
                normalizeError(error),
            ),
        );
    }
}

function* insertImage({
    editor,
    formName,
    config,
    files,
    processPhotoUploadOnFormPage,
}: ArticleEditInsertImageTriggerAction & XHRAction) {
    try {
        const currentValuesBefore: ArticleFormData = yield select(
            getFormValues(ARTICLE_CREATE_EDIT_FORM_NAME),
        );
        const photosBefore = currentValuesBefore.photos;

        yield put(articleEditInsertImageApi.request(config, files));

        yield put(photosUploadTrigger(formName, config, files, processPhotoUploadOnFormPage));
        const photoPreview = [];
        const photoPreviewErrors = [];
        const pendingUploads = [];
        const dummyError: XHRFormError = {
            name: "ERROR_COULD_NOT_PERFORM_ACTION",
            message: ERROR_COULD_NOT_PERFORM_ACTION,
            status: 400,
        };

        for (let i = 0; i < files.length; i++) {
            try {
                const onePhotoPreview = yield call(getPhotoPreview, files[i]);
                photoPreview.push(onePhotoPreview);
                pendingUploads.push(files[i]);
            } catch (error) {
                photoPreviewErrors.push(error);
            }
        }

        // If no photoPreview received, report error, else continue and notify later
        if (photoPreview.length < 1 && photoPreviewErrors.length > 0) {
            scrollDocumentTo("bottom");
            yield handleXHRFormErrorSaga(
                ARTICLE_CREATE_EDIT_FORM_NAME,
                dummyError as XHRFormError,
                logger,
            );
            yield put(articleEditInsertImageApi.failure(config, files, normalizeError(dummyError)));
            return;
        }

        const normalizedResponse: ArticleEditInsertImageNormalizedResponse = normalize(
            { photoPreview },
            { photoPreview: [PhotoPreviewSchema] },
        );

        const done = yield insertUploadingImagesToTheEditor(
            editor,
            currentValuesBefore.editorType,
            pendingUploads,
            photosBefore,
            photoPreviewErrors,
        );
        if (!done) {
            scrollDocumentTo("bottom");
            yield handleXHRFormErrorSaga(
                ARTICLE_CREATE_EDIT_FORM_NAME,
                dummyError as XHRFormError,
                logger,
            );
            yield put(articleEditInsertImageApi.failure(config, files, normalizeError(dummyError)));
        }

        yield put(articleEditInsertImageApi.success(config, files, normalizedResponse));
    } catch (error) {
        yield handleXHRFormErrorSaga(ARTICLE_CREATE_EDIT_FORM_NAME, error as XHRFormError, logger);
        yield put(articleEditInsertImageApi.failure(config, files, error));
    }
}

export default function* root() {
    yield all([
        takeLatest(ARTICLE_EDIT_FETCH_TRIGGER, fetchArticleEdit),
        takeLatest(ARTICLE_EDIT_SAVE_ARTICLE_TRIGGER, saveArticle),
        takeEvery(ARTICLE_EDIT_CHANGE_EDITOR_TYPE_TRIGGER, changeEditorType),
        takeEvery(ARTICLE_EDIT_INSERT_IMAGE_TRIGGER, insertImage),
    ]);
}
